import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

// Declaração de tipos para o ChatConnect SDK
declare global {
  interface Window {
    chatwootSDK?: {
      run: (config: {
        websiteToken: string;
        baseUrl: string;
      }) => void;
    };
    $chatwoot?: {
      setUser?: (userId: string, userInfo: {
        email?: string;
        name?: string;
        avatar_url?: string;
        phone_number?: string;
      }) => void;
      setCustomAttributes?: (attributes: Record<string, any>) => void;
      reset?: () => void;
    };
  }
}

/**
 * ChatConnectWidget - Componente para integração do chat de suporte
 * 
 * Funcionalidades:
 * - Carregamento assíncrono do SDK do ChatConnect
 * - Integração com contexto de autenticação
 * - Passa informações do usuário autenticado para o chat
 * - Limpa dados ao deslogar
 */
export const ChatConnectWidget = () => {
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    // Só carregar o chat se o usuário estiver autenticado
    if (!isAuthenticated) {
      return;
    }

    // Configuração do ChatConnect
    const BASE_URL = 'https://chatconnect.cleoia.com.br';
    const WEBSITE_TOKEN = 'sNw9QJKtTE71mU7PDhZ3AJyZ';

    // Verificar se o script já foi carregado
    const existingScript = document.querySelector(`script[src="${BASE_URL}/packs/js/sdk.js"]`);
    
    if (existingScript) {
      // Script já existe, apenas inicializar com os dados do usuário
      initializeChatWithUser();
      return;
    }

    // Criar e adicionar o script do ChatConnect
    const script = document.createElement('script');
    script.src = `${BASE_URL}/packs/js/sdk.js`;
    script.defer = true;
    script.async = true;

    script.onload = () => {
      console.log('✅ [ChatConnect] SDK carregado com sucesso');
      
      // Inicializar o ChatConnect
      if (window.chatwootSDK) {
        window.chatwootSDK.run({
          websiteToken: WEBSITE_TOKEN,
          baseUrl: BASE_URL
        });

        console.log('✅ [ChatConnect] Widget inicializado');

        // Aguardar um momento para o widget estar pronto
        setTimeout(() => {
          initializeChatWithUser();
          setupAutoFill();
          setupChatEventListeners();
        }, 1000);
      }
    };

    script.onerror = () => {
      console.error('❌ [ChatConnect] Erro ao carregar o SDK');
    };

    // Adicionar o script ao documento
    document.body.appendChild(script);

    // Cleanup: resetar o chat ao desmontar
    return () => {
      if (window.$chatwoot?.reset) {
        window.$chatwoot.reset();
        console.log('🔄 [ChatConnect] Chat resetado');
      }
    };
  }, [isAuthenticated]);

  // Atualizar dados do usuário quando mudar
  useEffect(() => {
    if (isAuthenticated && user) {
      initializeChatWithUser();
      setupAutoFill();
    }
  }, [user, isAuthenticated]);

  /**
   * Inicializa o chat com os dados do usuário autenticado
   */
  const initializeChatWithUser = () => {
    if (!user || !window.$chatwoot) {
      return;
    }

    try {
      // Definir informações do usuário no chat
      if (window.$chatwoot.setUser) {
        window.$chatwoot.setUser(String(user.id), {
          email: user.email,
          name: user.name
        });

        console.log('✅ [ChatConnect] Usuário configurado:', {
          id: user.id,
          name: user.name,
          email: user.email
        });
      }

      // Definir atributos customizados
      if (window.$chatwoot.setCustomAttributes) {
        const customAttributes: Record<string, any> = {
          user_id: user.id,
          role: user.role,
          client_id: user.client_id || null
        };

        // Adicionar informações do cliente se disponível
        if (user.client) {
          customAttributes.client_name = user.client.legal_name || user.client.trade_name;
          customAttributes.client_account_id = user.client.account_id || null;
          customAttributes.company_name = user.client.trade_name || user.client.legal_name;
          customAttributes.legal_name = user.client.legal_name;
          customAttributes.trade_name = user.client.trade_name;
          
          // Configurar preenchimento automático do formulário de chat
          customAttributes.auto_fill_company = user.client.trade_name || user.client.legal_name;
          customAttributes.auto_fill_email = user.email;
          customAttributes.auto_fill_name = user.name;
        }

        window.$chatwoot.setCustomAttributes(customAttributes);

        console.log('✅ [ChatConnect] Atributos customizados configurados:', customAttributes);
      }
    } catch (error) {
      console.error('❌ [ChatConnect] Erro ao configurar usuário:', error);
    }
  };

  /**
   * Configura o preenchimento automático do formulário de chat
   */
  const setupAutoFill = () => {
    if (!user || !user.client) {
      return;
    }

    // Aguardar o formulário estar disponível e configurar preenchimento automático
    const setupAutoFillInterval = setInterval(() => {
      const chatWidget = document.querySelector('#chatwoot-widget');
      if (!chatWidget) {
        return;
      }

      // Aguardar o formulário de contato aparecer
      const contactForm = document.querySelector('[data-testid="contact-form"]') || 
                         document.querySelector('.contact-form') ||
                         document.querySelector('form');
      
      if (contactForm) {
        console.log('✅ [ChatConnect] Formulário de contato encontrado, configurando preenchimento automático');
        
        // Procurar pelo campo de empresa/nome da associação
        const companyField = document.querySelector('input[placeholder*="Associação"]') ||
                            document.querySelector('input[placeholder*="Empresa"]') ||
                            document.querySelector('input[name*="company"]') ||
                            document.querySelector('input[name*="empresa"]');
        
        if (companyField && companyField instanceof HTMLInputElement) {
          const companyName = user.client.trade_name || user.client.legal_name;
          if (companyName && !companyField.value) {
            companyField.value = companyName;
            companyField.dispatchEvent(new Event('input', { bubbles: true }));
            companyField.dispatchEvent(new Event('change', { bubbles: true }));
            
            console.log('✅ [ChatConnect] Campo empresa preenchido automaticamente:', companyName);
          }
        }

        // Procurar pelo campo de nome
        const nameField = document.querySelector('input[placeholder*="Nome"]') ||
                         document.querySelector('input[name*="name"]');
        
        if (nameField && nameField instanceof HTMLInputElement) {
          if (user.name && !nameField.value) {
            nameField.value = user.name;
            nameField.dispatchEvent(new Event('input', { bubbles: true }));
            nameField.dispatchEvent(new Event('change', { bubbles: true }));
            
            console.log('✅ [ChatConnect] Campo nome preenchido automaticamente:', user.name);
          }
        }

        // Procurar pelo campo de email
        const emailField = document.querySelector('input[type="email"]') ||
                          document.querySelector('input[placeholder*="email"]') ||
                          document.querySelector('input[name*="email"]');
        
        if (emailField && emailField instanceof HTMLInputElement) {
          if (user.email && !emailField.value) {
            emailField.value = user.email;
            emailField.dispatchEvent(new Event('input', { bubbles: true }));
            emailField.dispatchEvent(new Event('change', { bubbles: true }));
            
            console.log('✅ [ChatConnect] Campo email preenchido automaticamente:', user.email);
          }
        }

        // Limpar o intervalo pois já configuramos
        clearInterval(setupAutoFillInterval);
      }
    }, 500);

    // Limpar o intervalo após 10 segundos para evitar loop infinito
    setTimeout(() => {
      clearInterval(setupAutoFillInterval);
    }, 10000);
  };

  /**
   * Configura listeners para eventos do chat
   */
  const setupChatEventListeners = () => {
    if (!user || !user.client) {
      return;
    }

    // Listener para quando o chat é aberto
    const handleChatOpen = () => {
      console.log('✅ [ChatConnect] Chat aberto, configurando preenchimento automático');
      setTimeout(() => {
        setupAutoFill();
      }, 500);
    };

    // Listener para quando o formulário de contato é mostrado
    const handleContactFormShow = () => {
      console.log('✅ [ChatConnect] Formulário de contato mostrado, preenchendo campos');
      setTimeout(() => {
        setupAutoFill();
      }, 300);
    };

    // Adicionar listeners para eventos do ChatConnect
    if (window.$chatwoot) {
      // Tentar usar a API de eventos do ChatConnect se disponível
      if (typeof window.$chatwoot.on === 'function') {
        window.$chatwoot.on('chat:opened', handleChatOpen);
        window.$chatwoot.on('contact-form:opened', handleContactFormShow);
        console.log('✅ [ChatConnect] Event listeners configurados');
      } else {
        // Fallback: usar MutationObserver para detectar mudanças no DOM
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach((node) => {
                if (node instanceof Element) {
                  // Verificar se foi adicionado um formulário de contato
                  const contactForm = node.querySelector('[data-testid="contact-form"]') ||
                                    node.querySelector('.contact-form') ||
                                    (node.tagName === 'FORM' ? node : null);
                  
                  if (contactForm) {
                    console.log('✅ [ChatConnect] Formulário detectado via MutationObserver');
                    setTimeout(() => {
                      setupAutoFill();
                    }, 300);
                  }
                }
              });
            }
          });
        });

        // Observar mudanças no body
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        console.log('✅ [ChatConnect] MutationObserver configurado como fallback');
      }
    }
  };

  // Componente não renderiza nada (o widget é injetado pelo SDK)
  return null;
};

